import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlignCenter, AlignLeft, AlignRight, Bold, Heading1, Heading2, ImagePlus, Italic, Link, List, ListOrdered, Redo2, RemoveFormatting, Underline, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { MakeWithAIDialog } from '../../components/ai/MakeWithAIDialog';
import { applyDocumentSelection, captureDocumentSelection, captureTextSelection, replaceText, type AssistSnapshot } from '../../components/ai/contentAssist';
import { pagesApi } from '../../api/pages';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import type { Page } from '../../types';

interface PageFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: Page | null;
}

interface FormState {
  name: string;
  html: string;
  redirectUrl: string;
}

const previewPolicy = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; style-src 'unsafe-inline' https: http:; img-src data: blob: https: http:; font-src data: https: http:; media-src data: https: http:; form-action 'none'; base-uri 'none';">`;

const logoWidths = { small: 72, medium: 140, large: 220 } as const;
type LogoSize = keyof typeof logoWidths;
type LogoAlign = 'left' | 'center' | 'right';

function insertLogo(doc: Document, src: string, align: LogoAlign, width: number) {
  const block = doc.createElement('p');
  block.style.textAlign = align;
  block.style.margin = '0 0 16px';
  const image = doc.createElement('img');
  image.src = src;
  image.alt = 'Logo';
  image.width = width;
  image.style.width = `${width}px`;
  image.style.height = 'auto';
  image.style.maxWidth = '100%';
  image.style.display = 'inline-block';
  block.appendChild(image);
  doc.body.insertBefore(block, doc.body.firstChild);
}

function htmlFromVisualDocument(doc: Document, source: string): string {
  const clone = doc.documentElement.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('meta[http-equiv="Content-Security-Policy" i]').forEach((node) => node.remove());
  const type = /<!doctype html/i.test(source) ? '<!DOCTYPE html>' : '';
  return `${type}${clone.outerHTML}`;
}

function openLandingPreview(html: string): boolean {
  const popup = window.open('', 'awarenow-page-preview', 'popup,width=1100,height=800');
  if (!popup) return false;
  popup.document.open();
  popup.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Landing page preview</title><style>html,body{margin:0;height:100%;background:#fff}iframe{border:0;width:100%;height:100%;}</style></head><body></body></html>');
  popup.document.close();
  const frame = popup.document.createElement('iframe');
  frame.title = 'Landing page preview';
  frame.setAttribute('sandbox', '');
  frame.referrerPolicy = 'no-referrer';
  frame.srcdoc = `${previewPolicy}${html}`;
  popup.document.body.replaceChildren(frame);
  popup.focus();
  return true;
}

function PageForm({
  page,
  onClose,
  onPendingChange,
}: {
  page: Page | null;
  onClose: () => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => ({
    name: page?.name ?? '',
    html: page?.html ?? '',
    redirectUrl: page?.redirect_url ?? '',
  }));
  const [error, setError] = useState<string | null>(null);
  const [visual, setVisual] = useState(false);
  const [visualHtml, setVisualHtml] = useState('');
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState('');
  const [logoSize, setLogoSize] = useState<LogoSize>('medium');
  const [logoAlign, setLogoAlign] = useState<LogoAlign>('center');
  const visualFrame = useRef<HTMLIFrameElement>(null);
  const [assist, setAssist] = useState<AssistSnapshot | null>(null);
  const htmlRef = useRef(form.html);
  htmlRef.current = form.html;

  const readVisualHtml = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.documentElement) return htmlRef.current;
    const next = htmlFromVisualDocument(doc, htmlRef.current);
    htmlRef.current = next;
    return next;
  };

  const currentHtml = () => (visual ? readVisualHtml() : htmlRef.current);

  const saveMutation = useMutation({
    mutationFn: () => {
      const html = currentHtml();
      const payload = {
        name: form.name,
        html,
        redirect_url: form.redirectUrl,
        capture_credentials: page?.capture_credentials ?? false,
        capture_passwords: page?.capture_passwords ?? false,
      };
      return page ? pagesApi.update(page.id, payload) : pagesApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save landing page');
    },
  });

  // Keep the parent modal aware of in-flight saves so it can refuse to close.
  useEffect(() => {
    onPendingChange(saveMutation.isPending);
  }, [saveMutation.isPending, onPendingChange]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (saveMutation.isPending) return;
    setError(null);
    if (visual) setForm((current) => ({ ...current, html: currentHtml() }));
    saveMutation.mutate();
  };

  const enableVisualEditing = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.body) return;
    doc.body.contentEditable = 'true';
    doc.body.addEventListener('input', () => {
      const next = readVisualHtml();
      setForm((current) => ({ ...current, html: next }));
    });
  };

  const formatVisual = (command: string, value?: string) => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc) return;
    visualFrame.current?.contentWindow?.focus();
    doc.execCommand(command, false, value);
    setForm((current) => ({ ...current, html: readVisualHtml() }));
  };

  const chooseLogo = (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      setError('Use a PNG, JPEG, GIF, or WebP logo.');
      return;
    }
    if (file.size > 400 * 1024) {
      setError('Logo must be 400 KB or smaller.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setLogoSrc(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  };

  const placeLogo = () => {
    const doc = visualFrame.current?.contentDocument;
    if (!doc?.body || !logoSrc) return;
    insertLogo(doc, logoSrc, logoAlign, logoWidths[logoSize]);
    setForm((current) => ({ ...current, html: readVisualHtml() }));
  };

  const captureLandingAssist = () => {
    const html = currentHtml();
    if (visual && visualFrame.current?.contentDocument) {
      const selected = captureDocumentSelection(visualFrame.current.contentDocument);
      setAssist({
        target: 'landing_html',
        label: 'Landing page HTML',
        scope: selected ? 'selection' : 'field',
        selectedText: selected?.selectedText ?? '',
        beforeText: '',
        afterText: '',
        documentHtml: html,
        documentText: '',
        fieldValue: html,
        selectionStart: null,
        selectionEnd: null,
        range: selected?.range ?? null,
        metadata: { name: form.name },
      });
      return;
    }
    const element = document.getElementById('page-html') as HTMLTextAreaElement | null;
    const captured = captureTextSelection(html, element?.selectionStart ?? null, element?.selectionEnd ?? null);
    if (captured.selectedText.length > 20000) {
      setError('Select 20,000 characters or fewer.');
      return;
    }
    if (captured.scope === 'field' && html.length > 100000) {
      setError('This field is too long for AI assistance.');
      return;
    }
    setError(null);
    setAssist({
      target: 'landing_html',
      label: 'Landing page HTML',
      ...captured,
      documentHtml: html,
      documentText: '',
      fieldValue: html,
      range: null,
      metadata: { name: form.name },
    });
  };

  const applyLandingAssist = (replacement: string) => {
    if (!assist) return;
    if (assist.range && visualFrame.current?.contentDocument) {
      applyDocumentSelection(visualFrame.current.contentDocument, assist.range, replacement);
      const next = readVisualHtml();
      htmlRef.current = next;
      setForm((current) => ({ ...current, html: next }));
    } else {
      const next = replaceText(assist.fieldValue, assist.selectionStart, assist.selectionEnd, replacement);
      htmlRef.current = next;
      setForm((current) => ({ ...current, html: next }));
      if (visual) setVisualHtml(`${previewPolicy}${next}`);
    }
    setAssist(null);
  };

  const toggleVisual = () => {
    if (visual) {
      setForm((current) => ({ ...current, html: readVisualHtml() }));
      setVisual(false);
      return;
    }
    setVisualHtml(`${previewPolicy}${htmlRef.current}`);
    setVisual(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label" htmlFor="page-name">Page name</label>
        <input
          id="page-name"
          className="input"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          required
        />
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <label className="label mb-0" htmlFor="page-html">HTML content</label>
          <Button type="button" variant="secondary" size="sm" disabled={saveMutation.isPending} onMouseDown={(event) => { event.preventDefault(); captureLandingAssist(); }}>
            Make with AI
          </Button>
          <Button type="button" variant="secondary" size="sm" disabled={saveMutation.isPending} onClick={toggleVisual}>
            {visual ? 'HTML source' : 'WYSIWYG'}
          </Button>
        </div>
        {visual ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" size="sm" aria-label="Bold" onClick={() => formatVisual('bold')}><Bold className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Italic" onClick={() => formatVisual('italic')}><Italic className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Underline" onClick={() => formatVisual('underline')}><Underline className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Heading 1" onClick={() => formatVisual('formatBlock', '<h1>')}><Heading1 className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Heading 2" onClick={() => formatVisual('formatBlock', '<h2>')}><Heading2 className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Align left" onClick={() => formatVisual('justifyLeft')}><AlignLeft className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Align center" onClick={() => formatVisual('justifyCenter')}><AlignCenter className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Align right" onClick={() => formatVisual('justifyRight')}><AlignRight className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Bulleted list" onClick={() => formatVisual('insertUnorderedList')}><List className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Numbered list" onClick={() => formatVisual('insertOrderedList')}><ListOrdered className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Link" onClick={() => { const url = window.prompt('Link URL'); if (url) formatVisual('createLink', url); }}><Link className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Clear formatting" onClick={() => formatVisual('removeFormat')}><RemoveFormatting className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Undo" onClick={() => formatVisual('undo')}><Undo2 className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-label="Redo" onClick={() => formatVisual('redo')}><Redo2 className="h-4 w-4" /></Button>
              <Button type="button" variant="secondary" size="sm" aria-pressed={logoOpen} onClick={() => setLogoOpen((open) => !open)}><ImagePlus className="h-4 w-4" /> Logo</Button>
            </div>
            {logoOpen && (
              <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 p-3">
                <label className="text-sm">
                  Upload logo
                  <input className="mt-1 block text-sm" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(event) => chooseLogo(event.target.files?.[0])} />
                </label>
                <label className="text-sm">
                  Size
                  <select className="input mt-1" aria-label="Logo size" value={logoSize} onChange={(event) => setLogoSize(event.target.value as LogoSize)}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </label>
                <div className="flex gap-2">
                  <Button type="button" variant={logoAlign === 'left' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('left')}>Left</Button>
                  <Button type="button" variant={logoAlign === 'center' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('center')}>Center</Button>
                  <Button type="button" variant={logoAlign === 'right' ? 'primary' : 'secondary'} size="sm" onClick={() => setLogoAlign('right')}>Right</Button>
                </div>
                <Button type="button" size="sm" disabled={!logoSrc} onClick={placeLogo}>Insert logo</Button>
                {logoSrc && <img src={logoSrc} alt="Selected logo" className="h-10 w-auto rounded border border-gray-200 bg-white p-1" />}
              </div>
            )}
            <iframe
              ref={visualFrame}
              title="Landing page visual editor"
              sandbox="allow-same-origin"
              srcDoc={visualHtml}
              onLoad={enableVisualEditing}
              className="w-full min-h-80 rounded-lg border border-gray-200 bg-white"
            />
          </div>
        ) : (
          <textarea
            id="page-html"
            className="input min-h-48 font-mono"
            value={form.html}
            onChange={(event) => setForm({ ...form, html: event.target.value })}
            required
          />
        )}
      </div>
      <div>
        <label className="label" htmlFor="page-redirect-url">Redirect URL (optional)</label>
        <input
          id="page-redirect-url"
          className="input"
          type="url"
          value={form.redirectUrl}
          onChange={(event) => setForm({ ...form, redirectUrl: event.target.value })}
        />
      </div>
      <div>
        <p className="label">Preview</p>
        <iframe
          title="Landing page preview"
          sandbox=""
          srcDoc={`${previewPolicy}${form.html}`}
          className="w-full min-h-48 rounded-lg border border-gray-200 bg-white"
        />
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <MakeWithAIDialog
        open={assist !== null}
        snapshot={assist}
        onApply={applyLandingAssist}
        onClose={() => setAssist(null)}
      />
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="secondary"
          disabled={saveMutation.isPending}
          onClick={() => {
            if (!openLandingPreview(form.html)) setError('Allow pop-ups to open the landing page preview.');
          }}
        >
          Preview
        </Button>
        <Button type="button" variant="secondary" disabled={saveMutation.isPending} onClick={onClose}>Cancel</Button>
        <Button type="submit" loading={saveMutation.isPending}>Save page</Button>
      </div>
    </form>
  );
}

export function PageFormModal({ isOpen, onClose, page }: PageFormModalProps) {
  const [saving, setSaving] = useState(false);
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={page ? `Edit Landing Page — ${page.name}` : 'New Landing Page'}
      size="xl"
    >
      <PageForm key={page?.id ?? 'new'} page={page} onClose={onClose} onPendingChange={setSaving} />
    </Modal>
  );
}
